/**
 * Vista de detalle del evento reutilizable.
 *
 * Este componente presentacional es el ÚNICO lugar donde se define la
 * apariencia del evento que verá el socio. Se consume desde:
 *   - `screens/app/EventoDetalle.js` (portal socio real).
 *   - `screens/admin/eventos/PasoPublicacion.js` (Vista Previa admin).
 *
 * Prop `evento` debe venir ya adaptado (`adaptarEventoParaSocio`).
 * Prop `mode`:
 *   - "socio" (default): botones activos, navegación real.
 *   - "preview": botón Comprar deshabilitado y sin navegación.
 * Prop `onBack` opcional: si viene, muestra el botón "Regresar".
 * Prop `onComprar` opcional: se dispara al presionar el botón principal.
 */
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  MapPin,
  Ticket,
  Shirt,
  Info,
  Share2,
  CalendarPlus,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { EVENTO_DETALLE } from "@/constants/testIds";
import EventoImagen from "@/components/common/EventoImagen";
import { formatearFechaLarga } from "@/utils/fechas";

const estadoStyles = {
  "Venta disponible":
    "bg-accent/15 text-accent border-accent/40",
  "Últimos lugares":
    "bg-primary/10 text-primary dark:text-accent border-primary/30 dark:border-accent/40",
  Próximamente:
    "bg-muted text-muted-foreground border-border",
  Agotado:
    "bg-destructive/10 text-destructive border-destructive/30",
};

export default function EventoDetalleView({
  evento,
  mode = "socio",
  onBack,
  onComprar,
}) {
  if (!evento) return null;
  const preview = mode === "preview";
  const badge = estadoStyles[evento.estado] || estadoStyles["Próximamente"];

  const bloques = [
    {
      key: "descripcion",
      icon: Info,
      titulo: "Descripción del evento",
      contenido: evento.descripcion,
      testId: EVENTO_DETALLE.descripcion,
    },
    ...(evento.codigoVestimenta
      ? [
          {
            key: "vestimenta",
            icon: Shirt,
            titulo: "Código de vestimenta",
            contenido: evento.codigoVestimenta,
            testId: EVENTO_DETALLE.vestimenta,
          },
        ]
      : []),
    ...(evento.informacionAdicional
      ? [
          {
            key: "informacion",
            icon: Info,
            titulo: "Información adicional",
            contenido: evento.informacionAdicional,
            testId: EVENTO_DETALLE.informacion,
          },
        ]
      : []),
  ];

  const acciones = preview
    ? []
    : [
        { label: "Compartir evento", icon: Share2, testId: EVENTO_DETALLE.compartir },
        { label: "Agregar al calendario", icon: CalendarPlus, testId: EVENTO_DETALLE.calendario },
      ];

  const imagenSrc = evento.imagen || evento.imagenPrincipal || "";

  return (
    <div
      data-testid={EVENTO_DETALLE.screen}
      className="flex flex-col gap-8 -mx-6 md:-mx-8 -mt-6"
    >
      {onBack && (
        <div className="px-6 md:px-8 pt-2">
          <button
            type="button"
            data-testid={EVENTO_DETALLE.btnRegresar}
            onClick={onBack}
            aria-label="Regresar"
            className="inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-medium border hairline bg-card/80 backdrop-blur text-foreground hover:bg-muted transition-all active:scale-[0.98]"
          >
            <span className="w-7 h-7 grid place-items-center rounded-full bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            </span>
            Regresar
          </button>
        </div>
      )}

      <div className="relative w-full aspect-[16/10] md:aspect-[21/9] overflow-hidden bg-muted">
        <EventoImagen
          src={imagenSrc}
          eventoId={evento.id}
          alt={evento.nombre || "Evento"}
          loading="eager"
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, transparent 45%, hsl(var(--background) / 0.85) 100%)",
          }}
        />
      </div>

      <div className="px-6 md:px-8 flex flex-col gap-8">
        <header className="flex flex-col gap-5 animate-fade-up -mt-4">
          <div className="flex items-start justify-between gap-4">
            <h1
              data-testid={EVENTO_DETALLE.nombre}
              className="font-heading text-3xl sm:text-4xl leading-tight text-foreground"
            >
              {evento.nombre}
            </h1>
            <span
              data-testid={EVENTO_DETALLE.estado}
              className={cn(
                "shrink-0 px-3 py-1 rounded-full text-[11px] font-medium border",
                badge,
              )}
            >
              {evento.estado}
            </span>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <li className="flex items-center gap-3 rounded-xl border hairline bg-card p-4">
              <CalendarDays className="w-4 h-4 text-accent" strokeWidth={1.6} />
              <div className="flex flex-col">
                <span className="overline text-muted-foreground">Fecha</span>
                <span data-testid={EVENTO_DETALLE.fecha} className="text-foreground mt-1">
                  {formatearFechaLarga(evento.fecha) || "—"}
                </span>
              </div>
            </li>
            <li className="flex items-center gap-3 rounded-xl border hairline bg-card p-4">
              <Clock className="w-4 h-4 text-accent" strokeWidth={1.6} />
              <div className="flex flex-col">
                <span className="overline text-muted-foreground">Hora</span>
                <span data-testid={EVENTO_DETALLE.hora} className="text-foreground mt-1">
                  {evento.hora || "—"}
                </span>
              </div>
            </li>
            <li className="flex items-center gap-3 rounded-xl border hairline bg-card p-4">
              <MapPin className="w-4 h-4 text-accent" strokeWidth={1.6} />
              <div className="flex flex-col">
                <span className="overline text-muted-foreground">Salón</span>
                <span data-testid={EVENTO_DETALLE.salon} className="text-foreground mt-1">
                  {evento.salon || "—"}
                </span>
              </div>
            </li>
            <li className="flex items-start gap-3 rounded-xl border hairline bg-card p-4">
              <Ticket className="w-4 h-4 text-accent mt-0.5" strokeWidth={1.6} />
              <div className="flex flex-col w-full">
                {evento.modalidad === "ACCESO_GENERAL" ? (
                  (() => {
                    const r = evento.accesoResumen;
                    const fmtP = (n) => `$${Number(n || 0).toLocaleString("es-MX")} MXN`;
                    return (
                      <>
                        <span className="overline text-muted-foreground">
                          {r && r.multiple ? "Precios por salón" : "Acceso general"}
                        </span>
                        <span
                          data-testid={EVENTO_DETALLE.precio}
                          className="font-heading text-lg text-foreground mt-1"
                        >
                          {!r
                            ? "Acceso general"
                            : r.multiple
                              ? `Desde ${fmtP(r.precioDesde)}`
                              : fmtP(r.precioUnico ?? r.precioDesde)}
                        </span>
                        {r && Array.isArray(r.salones) && r.multiple && (
                          <ul
                            data-testid={EVENTO_DETALLE.preciosList}
                            className="mt-2 flex flex-col gap-1"
                          >
                            {r.salones.map((s) => (
                              <li
                                key={s.id}
                                className="flex items-center justify-between gap-3 text-sm"
                              >
                                <span className="text-muted-foreground">{s.salon}</span>
                                <span className="font-heading text-foreground">
                                  {fmtP(s.precioPorBoleto)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <span className="text-xs text-muted-foreground mt-1">
                          Elige tus boletos por salón al comprar.
                        </span>
                      </>
                    );
                  })()
                ) : (
                  <>
                    <span className="overline text-muted-foreground">
                      {evento.precioMultiple
                        ? "Precios por salón"
                        : "Precio del boleto"}
                    </span>
                    {evento.precioMultiple &&
                    Array.isArray(evento.preciosPorSalon) ? (
                      <ul
                        data-testid={EVENTO_DETALLE.preciosList}
                        className="mt-1 flex flex-col gap-1"
                      >
                        {evento.preciosPorSalon.map((p) => (
                          <li
                            key={p.salon}
                            className="flex items-center justify-between gap-3 text-sm"
                          >
                            <span className="text-muted-foreground">{p.salon}</span>
                            <span className="font-heading text-foreground">
                              {p.precio}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span
                        data-testid={EVENTO_DETALLE.precio}
                        className="font-heading text-lg text-foreground mt-1"
                      >
                        {evento.precio || "—"}
                      </span>
                    )}
                  </>
                )}
              </div>
            </li>
          </ul>
        </header>

        <button
          type="button"
          data-testid={EVENTO_DETALLE.btnComprar}
          onClick={preview ? undefined : onComprar}
          disabled={preview}
          className={cn(
            "w-full inline-flex items-center justify-center gap-2 rounded-full",
            "px-6 py-4 text-sm font-semibold tracking-wide uppercase",
            "bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground",
            "ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft",
            "transition-all duration-200",
            preview
              ? "opacity-70 cursor-not-allowed"
              : "active:scale-[0.99] hover:opacity-95",
          )}
        >
          {preview ? "Comprar boletos · vista previa" : "Comprar boletos"}
          <ChevronRight className="w-4 h-4" strokeWidth={2} />
        </button>

        {preview && (
          <p className="text-[11px] text-muted-foreground text-center -mt-4">
            La vista previa no genera compras ni modifica el estado del evento.
          </p>
        )}

        <section className="flex flex-col gap-4">
          {bloques.map(({ key, icon: Icon, titulo, contenido, testId }) => (
            <article
              key={key}
              data-testid={testId}
              className="rounded-2xl border hairline bg-card p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 grid place-items-center rounded-lg bg-accent/10 text-accent">
                  <Icon className="w-4 h-4" strokeWidth={1.7} />
                </span>
                <h2 className="font-heading text-lg text-foreground">{titulo}</h2>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {contenido}
              </p>
            </article>
          ))}

          {acciones.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {acciones.map(({ label, icon: Icon, testId }) => (
                <button
                  key={label}
                  type="button"
                  data-testid={testId}
                  className="flex items-center justify-between gap-3 rounded-2xl border hairline bg-card p-5 text-left transition-colors hover:bg-muted"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-9 h-9 grid place-items-center rounded-xl bg-primary/5 dark:bg-accent/10 text-primary dark:text-accent">
                      <Icon className="w-4 h-4" strokeWidth={1.7} />
                    </span>
                    <span className="text-sm font-medium text-foreground">{label}</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
